import React from "react";

/**
 * Lightweight page banner used on /address, /enroll, plan pages, etc.
 *
 * Rolled back to the original clean look after testing the full-bleed
 * photo background — too dark for input-heavy forms, the labels were
 * unreadable. This version: clean white surface, small eyebrow, big
 * serif title, supporting subtitle. Easy to read, ethically clear,
 * works on every viewport.
 *
 * Props:
 *   eyebrow  - small kicker label above the title
 *   title    - main heading
 *   subtitle - one-line summary under the title
 *   as       - element override for the title (default 'div')
 *   testId   - data-testid root override
 */
export default function PageBanner({
  eyebrow,
  title,
  subtitle,
  as: Tag = "div",
  testId = "page-banner",
}) {
  return (
    <section
      className="relative bg-white border-b border-slate-100"
      data-testid={testId}
    >
      <div className="max-w-[1180px] mx-auto px-5 pt-10 pb-6 md:pt-14 md:pb-8">
        {eyebrow && (
          <div
            className="text-[10.5px] font-extrabold uppercase tracking-[0.22em] text-velcor-green mb-3"
            data-testid={`${testId}-eyebrow`}
          >
            {eyebrow}
          </div>
        )}
        <Tag
          className="font-black text-velcor-ink text-[28px] md:text-[38px] leading-[1.05] tracking-tight max-w-[820px]"
          style={{ fontFamily: '"Fraunces", "Source Serif Pro", Georgia, serif' }}
          data-testid={`${testId}-title`}
        >
          {title}
        </Tag>
        {subtitle && (
          <p
            className="text-slate-500 text-[14px] md:text-[15.5px] mt-3 max-w-[680px] leading-relaxed"
            data-testid={`${testId}-subtitle`}
          >
            {subtitle}
          </p>
        )}
      </div>
    </section>
  );
}
